import * as THREE from "three";
import type { VFX } from "../../../../../art/vfx.js";
import { Cell, Grid } from "@highmarch/domain";
import { WornSkins } from "../skins/wornSkins";
import { HubPet } from "./petHub";

/**
 * Every pet had, on the hub's lawn (Pets v3, docs/PETS.md "On screen": "every pet you own wanders the lawn (the one you
 * take into battle wears its gold ring); tap any for its sheet"). One HubPet per pet in WornSkins.petsOwned, made and
 * dropped as pets are caught; they keep off each other's cells. At most six small bodies on clips the hub's on-demand
 * renderer already draws at its ambient rate (docs/PERF.md: the hub's model share is the towers'; a pet is a fraction
 * of one). Presentation only.
 */
export class HubPets {
  readonly group = new THREE.Group();
  private readonly pets = new Map<string, HubPet>();
  private owned = "";
  private framing: { inShot: (x: number, z: number) => boolean; towers: readonly Cell[] } | null = null;
  /** The hub's sprite VFX for the coats' effects (HubFx.ambient), handed to every pet. */
  fx: (() => VFX | null) | null = null;

  constructor(private readonly grid: Grid, private readonly base: Cell, private readonly stands: readonly Cell[]) {}

  /** The pets on the lawn match the pets had (a catch adds one; a reset save drops them). */
  private sync(): void {
    const ids = WornSkins.petsOwned(), key = ids.join(",");
    if (key === this.owned) return;
    this.owned = key;
    for (const [id, p] of this.pets) if (!ids.includes(id)) { p.dispose(); p.group.removeFromParent(); this.pets.delete(id); }
    for (const id of ids) {
      if (this.pets.has(id)) continue;
      const p = new HubPet(this.grid, this.base, this.stands, id, (c, self) => this.takenBy(c, self));
      p.fx = this.fx;
      if (this.framing) p.frame(this.framing.inShot, this.framing.towers);
      this.pets.set(id, p);
      this.group.add(p.group);
    }
    for (const p of this.pets.values()) p.room(this.pets.size);
  }

  private takenBy(c: Cell, self: HubPet): boolean {
    for (const p of this.pets.values()) if (p !== self && p.at?.equals(c)) return true;
    return false;
  }

  /** The hub framed its shot again (HubPet.frame), for every pet. */
  frame(inShot: (x: number, z: number) => boolean, towers: readonly Cell[] = []): void {
    this.framing = { inShot, towers };
    for (const p of this.pets.values()) p.frame(inShot, towers);
  }

  update(dt: number): void {
    this.sync();
    for (const p of this.pets.values()) p.update(dt);
  }

  /** Pet `petId`'s bounds now (the anchor over it), or null when it is not on the lawn. */
  box(petId: string, out: THREE.Box3): THREE.Box3 | null { return this.pets.get(petId)?.box(out) ?? null; }
  /** The group of pet `petId` (the hub's celebrate burst), or null. */
  groupOf(petId: string): THREE.Object3D | null { return this.pets.get(petId)?.group ?? null; }
  /** One-shot: pet `petId` cheers (its upgrade finished). */
  cheer(petId: string): void { this.pets.get(petId)?.cheer(); }

  dispose(): void {
    for (const p of this.pets.values()) p.dispose();
    this.pets.clear();
    this.group.clear();
  }
}
